/**
 * Firing and projectile resolution (TRD-05 §4–§5).
 *
 * `beam`, `laser` and `chain` never become live projectiles — they resolve on the fire
 * tick and the renderer draws the result from the emitted events. Only `bullet`, `homing`
 * and `lob` travel, which keeps the projectile pool small even with a hundred fast
 * towers.
 *
 * ── Kind is not the whole weapon ──────────────────────────────────────────────────────
 * `projectileKind` says how a shot MOVES. It deliberately says nothing about how the
 * tower fires, because those are independent axes and folding them together is how a
 * kind enum turns into fifteen entries that each mean three things at once. The firing
 * approach lives in four orthogonal stats (TRD-05 §5.1):
 *
 *   salvo/spread    one trigger pull, N projectiles in a fan, damage divided
 *   burst           N trigger pulls back-to-back, then a long reload
 *   pierce          a travelling shot survives the enemies it passes through
 *   acceleration    a shot that launches slow and builds speed — a missile
 *
 * A homing salvo, a piercing lob and a bursting laser are all expressible without
 * touching `ProjectileKind`, which is the point.
 */
import {
  CHAIN_FALLOFF,
  CHAIN_RANGE_TILES,
  DT,
  LASER_HOLD_SLACK_TICKS,
  MISSILE_MAX_SPEED_FACTOR,
  PROJECTILE_LIFETIME_TICKS,
  SWEPT_COLLISION_TILES_PER_TICK,
  TICK_RATE,
} from '../constants.js';
import type { Enemy, EntityPool, Projectile, Tower, Zone } from '../entities.js';
import type { SimEvent } from '../events.js';
import { angleTo, cos, distSq, sin } from '../math.js';
import type { Rng } from '../rng.js';
import { NO_TARGET, type EntityId, type HitscanKind } from '../types.js';
import { applyDamage, rollOutgoingDamage, type DamageInput } from './damage.js';
import { effectiveRange } from './targeting.js';
import type { SpatialGrid } from './spatial.js';
import { applyOnHitHooks, resolveShotModifiers, type HookContext } from './hooks.js';
import { canSpawnZone } from './zones.js';

/**
 * Cooldown in whole ticks (R-05-09).
 *
 * Rounded UP so a faster attack speed can never produce a zero-tick cooldown and fire
 * every tick. Fractional accumulation is deliberately not an option here: carrying a
 * remainder across ticks drifts, and a tower that fires on tick 37 in one run and tick 38
 * in another has already broken replay.
 */
export function cooldownTicks(tower: Tower): number {
  const multiplier = tower.mods.attackSpeedMultiplier > 0 ? tower.mods.attackSpeedMultiplier : 1;
  return Math.max(1, Math.ceil((tower.attackInterval * TICK_RATE) / multiplier));
}

export interface FireContext {
  tick: number;
  rng: Rng;
  enemies: EntityPool<Enemy>;
  projectiles: EntityPool<Projectile>;
  /** Live zone attacks (B-18) — `zone` casts land here instead of the projectile pool. */
  zones: EntityPool<Zone>;
  grid: SpatialGrid;
  events: SimEvent[];
  allocateId: () => EntityId;
  scratch: EntityId[];
  /** Built once per tick — synergy hooks fire from inside the damage pipeline (TRD-08 §5). */
  hooks: HookContext;
}

/**
 * Ticks until this tower may fire again, honouring bursts (R-05-29).
 *
 * A burst tower spends `burstIntervalTicks` between the shots of a burst and the full
 * `cooldownTicks` after the last one. The state lives on the tower rather than in a
 * queue of scheduled shots because a queue outlives the tower that owns it — sell a
 * tower mid-burst and the pending shots have no `mods` to read.
 *
 * Called AFTER `fireTower`, so `burstRemaining` already reflects the shot just taken.
 */
export function advanceFireSchedule(tower: Tower): number {
  if (tower.burstCount <= 1) return cooldownTicks(tower);

  if (tower.burstRemaining > 0) {
    tower.burstRemaining--;
    // Never zero: a zero-tick gap would fire the whole burst on one tick, which is a
    // salvo, not a burst, and the two are meant to feel different.
    if (tower.burstRemaining > 0) return Math.max(1, tower.burstIntervalTicks);
    return cooldownTicks(tower);
  }

  tower.burstRemaining = tower.burstCount - 1;
  return Math.max(1, tower.burstIntervalTicks);
}

/**
 * Fire one tower at its current target.
 *
 * Order is fixed by R-05-10: count the shot, then hooks, then the projectile, then the
 * event. `shotsFired` increments before the hooks because "every 6th shot" reads it and
 * has to see the shot it is part of (R-05-11).
 */
export function fireTower(tower: Tower, ctx: FireContext): void {
  const target = ctx.enemies.get(tower.targetId);
  if (!target) {
    // A laser that loses its target loses its ramp, even if it never fired this tick.
    tower.beamStacks = 0;
    tower.beamTargetId = NO_TARGET;
    return;
  }

  tower.shotsFired++;

  const rolled = rollOutgoingDamage(tower, ctx.rng);
  const crit = rolled.crit;

  // On-fire hooks modify THIS shot only. `shotsFired` was incremented above precisely so
  // that "every 6th shot" sees the shot it is part of (R-05-11) — Marksman-6's railshot
  // is a property of the projectile leaving the barrel, not a persistent tower mod.
  const shot = resolveShotModifiers(tower);
  const amount = rolled.amount * shot.damageMultiplier;

  switch (tower.projectileKind) {
    case 'beam':
      // Instant, single target. The renderer draws a fading line from the event pair.
      emitBeam(tower, tower.x, tower.y, target.x, target.y, 'beam', 0, ctx);
      resolveHit(tower, target, amount, crit, 0, ctx);
      break;

    case 'laser':
      resolveLaser(tower, target, amount, crit, ctx);
      break;

    case 'chain':
      resolveChain(tower, target, amount, crit, ctx);
      break;

    case 'zone':
      spawnZone(tower, target, amount, crit, ctx);
      break;

    default:
      spawnSalvo(tower, target, amount, crit, ctx);
      break;
  }

  ctx.events.push({
    t: 'towerFired',
    kind: tower.damageKind,
    tower: tower.id,
    target: target.id,
    x: tower.x,
    y: tower.y,
    targetX: target.x,
    targetY: target.y,
  });
}

/**
 * A sustained channel (R-05-32).
 *
 * The tower's `attackInterval` is the damage CADENCE, not the time between shots — a
 * laser at 0.1 s ticks ten times a second for a tenth of the damage. That is what lets a
 * laser be a low-damage-per-hit weapon at all: R-05-18 floors every hit at 1 damage, so
 * a beam that resolved every tick could never deal less than 60 dps no matter what the
 * content said.
 *
 * Holding the beam on one enemy ramps it. Losing the target resets to zero, which is the
 * counterplay: a laser is the answer to one big thing, not to a wave of small ones.
 */
function resolveLaser(
  tower: Tower,
  target: Enemy,
  amount: number,
  crit: boolean,
  ctx: FireContext,
): void {
  if (tower.beamTargetId !== target.id) {
    tower.beamTargetId = target.id;
    tower.beamStacks = 0;
  }

  // Accumulated by multiplication against a running counter rather than an exponent —
  // `**` is banned in the sim for the precision reason in TRD-00 R-00-04.
  const ramp = Math.min(1 + tower.beamStacks * tower.beamRampPerTick, tower.beamRampMax);
  if (tower.beamStacks < LASER_RAMP_STACK_CAP) tower.beamStacks++;

  const hold = cooldownTicks(tower) + LASER_HOLD_SLACK_TICKS;
  emitBeam(tower, tower.x, tower.y, target.x, target.y, 'laser', hold, ctx);
  resolveHit(tower, target, amount * ramp, crit, 0, ctx);
}

/**
 * Stops `beamStacks` growing without bound on a boss that never dies.
 *
 * The ramp is already clamped by `beamRampMax`, but the COUNTER is part of the world
 * hash — an unbounded integer that only ever grows is a divergence waiting for a long
 * enough run, and it would serialise into a save as a number no one can explain.
 */
const LASER_RAMP_STACK_CAP = 600;

function emitBeam(
  tower: Tower,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  weapon: HitscanKind,
  holdTicks: number,
  ctx: FireContext,
): void {
  ctx.events.push({
    t: 'beamFired',
    tower: tower.id,
    x0,
    y0,
    x1,
    y1,
    kind: tower.damageKind,
    weapon,
    holdTicks,
    chainHop: 0,
  });
}

/**
 * One trigger pull, `salvo` projectiles, damage divided across them (R-05-28).
 *
 * The fan is symmetric around the aim line and computed from the index, never from the
 * RNG: a random spread would draw from the shared stream on every shot and make the
 * whole run's RNG sequence depend on how many towers happened to be firing, which is a
 * far more expensive determinism liability than a random-looking spread is worth.
 */
function spawnSalvo(
  tower: Tower,
  target: Enemy,
  amount: number,
  crit: boolean,
  ctx: FireContext,
): void {
  const count = tower.salvo > 1 ? Math.floor(tower.salvo) : 1;
  const aim = angleTo(tower.x, tower.y, target.x, target.y);
  const each = amount / count;

  for (let i = 0; i < count; i++) {
    // Single shot: dead centre. Fan: evenly spaced across the spread, ends inclusive.
    const offset = count === 1 ? 0 : tower.spreadRadians * (i / (count - 1) - 0.5);
    spawnProjectile(tower, target, each, crit, aim + offset, ctx);
  }
}

function spawnProjectile(
  tower: Tower,
  target: Enemy,
  amount: number,
  crit: boolean,
  angle: number,
  ctx: FireContext,
): void {
  const p = ctx.projectiles.spawn(ctx.allocateId());
  p.x = tower.x;
  p.y = tower.y;
  p.sourceTower = tower.id;
  p.targetId = target.id;
  p.damage = amount;
  p.kind = tower.damageKind;
  p.crit = crit;
  // Base pierce is a property of the shot the tower was authored to fire; `pierceCount`
  // is what synergies add on top. Reading only the mod (as this did) left every
  // content-authored piercing weapon silently single-target.
  p.pierceRemaining = tower.pierce + tower.mods.pierceCount;
  p.chainRemaining = 0;
  p.lifetime = PROJECTILE_LIFETIME_TICKS;
  p.radius = tower.projectileRadius;
  p.turnRate = tower.turnRate;
  p.accel = tower.projectileAcceleration;
  p.launchSpeed = tower.projectileSpeed;
  p.aoeRadius = tower.aoeRadius;
  // Only a lob commits to the ground. A homing missile carries an AoE too, and if it
  // detonated on arrival at this snapshot it would burst in empty air the moment its
  // target moved — which is every tick.
  p.detonateAtGround = tower.projectileKind === 'lob';

  // A lob commits to the ground position at fire time and does not follow the target
  // (R-05-13) — that is what makes dodging a mortar shell possible and the class legible.
  p.targetX = target.x;
  p.targetY = target.y;

  p.vx = cos(angle) * tower.projectileSpeed;
  p.vy = sin(angle) * tower.projectileSpeed;
}

/**
 * Anchor a zone at the target's position (B-18).
 *
 * Damage is committed at cast — rolled once, star-scaled, crit and all — exactly like
 * an AoE shot, so a zone outliving its sold caster still strikes what was paid for.
 * Skipped silently at the zone cap (the shot is spent), mirroring the drone cap's
 * skip-never-queue rule.
 */
function spawnZone(
  tower: Tower,
  target: Enemy,
  amount: number,
  crit: boolean,
  ctx: FireContext,
): void {
  if (!canSpawnZone(ctx.zones)) return;

  const z = ctx.zones.spawn(ctx.allocateId());
  z.ownerTower = tower.id;
  z.x = target.x;
  z.y = target.y;
  z.radius = tower.aoeRadius;
  z.damage = amount;
  z.kind = tower.damageKind;
  z.crit = crit;
  z.windupLeft = Math.max(1, tower.zoneWindupTicks);
  z.strikeEveryTicks = Math.max(1, tower.zoneStrikeEveryTicks);
  z.ticksLeft = Math.max(1, tower.zoneActiveTicks);
  z.sinceStrike = 0;
  z.trackSpeed = tower.zoneTrackSpeed;
  z.targetId = target.id;
}

/**
 * Chain lightning (R-05-16).
 *
 * Resolves entirely on the fire tick: hit the target, then repeatedly find the nearest
 * enemy not yet hit within `chainRange` of the last one. Falloff compounds per hop, and
 * one `projectileHit` is emitted per hop so TRD-04 can draw each arc separately.
 */
function resolveChain(
  tower: Tower,
  target: Enemy,
  amount: number,
  crit: boolean,
  ctx: FireContext,
): void {
  const hops = tower.mods.chainCount;
  const hit: EntityId[] = [target.id];

  emitBeam(tower, tower.x, tower.y, target.x, target.y, 'chain', 0, ctx);
  resolveHit(tower, target, amount, crit, 0, ctx);

  // Accumulated by repeated multiplication rather than `CHAIN_FALLOFF ** hop`: the
  // exponent operator's precision is engine-dependent, which is exactly the kind of
  // difference that shows up as a rejected replay on someone else's browser
  // (TRD-00 R-00-04).
  let falloff = 1;
  let from: Enemy = target;
  for (let hop = 1; hop <= hops; hop++) {
    const next = nearestUnhit(from, hit, ctx);
    if (!next) break;
    hit.push(next.id);
    falloff *= CHAIN_FALLOFF;
    // The arc's SOURCE is the previous enemy, which may be dead by the time the renderer
    // draws — so both endpoints ride the event rather than being looked up (TRD-00 §7).
    ctx.events.push({
      t: 'beamFired',
      tower: tower.id,
      x0: from.x,
      y0: from.y,
      x1: next.x,
      y1: next.y,
      kind: tower.damageKind,
      weapon: 'chain',
      holdTicks: 0,
      chainHop: hop,
    });
    resolveHit(tower, next, amount * falloff, crit, hop, ctx);
    from = next;
  }
}

/** Nearest not-yet-hit enemy within chain range, ties on lowest id (R-05-05, R-05-16). */
function nearestUnhit(from: Enemy, hit: readonly EntityId[], ctx: FireContext): Enemy | undefined {
  ctx.grid.query(from.x, from.y, CHAIN_RANGE_TILES, ctx.enemies, ctx.scratch);

  let best: Enemy | undefined;
  let bestDist = 0;
  for (let i = 0; i < ctx.scratch.length; i++) {
    const id = ctx.scratch[i] as EntityId;
    if (hit.includes(id)) continue;
    const candidate = ctx.enemies.get(id);
    if (!candidate) continue;
    const d = distSq(from.x, from.y, candidate.x, candidate.y);
    if (!best || d < bestDist || (d === bestDist && candidate.id < best.id)) {
      best = candidate;
      bestDist = d;
    }
  }
  return best;
}

/**
 * Land damage on one enemy, or on everything in an AoE radius around it.
 *
 * AoE applies FULL damage to every enemy in radius with no falloff (R-05-17). Falloff
 * reads as unreliable to a player — the same shot killing one enemy and not its
 * neighbour — and makes the numbers impossible to reason about while building.
 */
function resolveHit(
  tower: Tower,
  enemy: Enemy,
  amount: number,
  crit: boolean,
  chainHop: number,
  ctx: FireContext,
  aoeRadius = tower.aoeRadius,
): void {
  ctx.events.push({
    t: 'projectileHit',
    x: enemy.x,
    y: enemy.y,
    damage: amount,
    kind: tower.damageKind,
    crit,
    chainHop,
    aoeRadius,
    pierced: false,
    fromX: tower.x,
    fromY: tower.y,
  });

  const input: DamageInput = { amount, kind: tower.damageKind, crit, tower };

  if (aoeRadius <= 0) {
    applyDamage(enemy, input, ctx.tick, ctx.events);
    applyOnHitHooks(ctx.hooks, tower, enemy);
    return;
  }

  ctx.grid.query(enemy.x, enemy.y, aoeRadius, ctx.enemies, ctx.scratch);
  // Copied because `scratch` is reused by nested queries inside the loop below.
  const ids = ctx.scratch.slice();
  for (let i = 0; i < ids.length; i++) {
    const victim = ctx.enemies.get(ids[i] as EntityId);
    if (!victim) continue;
    applyDamage(victim, input, ctx.tick, ctx.events);
    // A projectile can outlive the tower that fired it, if it was sold mid-flight.
    if (tower) applyOnHitHooks(ctx.hooks, tower, victim);
  }
}

/**
 * Advance every live projectile one tick and resolve what it hits.
 *
 * Iterates by index over the pool's insertion-ordered array. Enemies killed here are only
 * tombstoned — `deathResolution` runs afterwards precisely so this loop never sees the
 * array change under it (R-05-20).
 */
export function stepProjectiles(ctx: FireContext, towers: EntityPool<Tower>): void {
  const items = ctx.projectiles.items;

  for (let i = 0; i < items.length; i++) {
    const p = items[i] as Projectile;
    if (!p.alive) continue;

    if (--p.lifetime <= 0) {
      ctx.projectiles.kill(p);
      continue;
    }

    const tower = towers.get(p.sourceTower);

    // Homing steers toward its target while that target lives. When it dies the
    // projectile keeps its last heading and behaves as a bullet rather than vanishing
    // (R-05-15) — a shot that disappears mid-flight reads as a bug to a player.
    if (p.turnRate > 0) {
      const target = ctx.enemies.get(p.targetId);
      if (target) steerToward(p, target);
    }

    // A missile launches slow and builds speed (R-05-31). The cap is what keeps the
    // swept collision test honest — an uncapped ramp over a 3 s lifetime reaches speeds
    // that step across the whole map in a tick.
    if (p.accel > 0) accelerate(p);

    const startX = p.x;
    const startY = p.y;
    p.x += p.vx * DT;
    p.y += p.vy * DT;

    // A lob commits to a ground point and detonates on arrival, hit or miss.
    if (p.detonateAtGround && p.targetId !== NO_TARGET && reachedTarget(p, startX, startY)) {
      detonate(p, tower, ctx);
      continue;
    }

    const victim = findCollision(p, startX, startY, ctx);
    if (!victim) continue;

    if (p.aoeRadius > 0) {
      detonate(p, tower, ctx);
      continue;
    }

    ctx.events.push({
      t: 'projectileHit',
      x: victim.x,
      y: victim.y,
      damage: p.damage,
      kind: p.kind,
      crit: p.crit,
      chainHop: 0,
      aoeRadius: 0,
      // Read BEFORE the decrement below: this shot goes on through, and the effect that
      // sells a piercing round is the one drawn on the target it did not stop at.
      pierced: p.pierceRemaining > 0,
      fromX: startX,
      fromY: startY,
    });
    applyDamage(
      victim,
      { amount: p.damage, kind: p.kind, crit: p.crit, tower },
      ctx.tick,
      ctx.events,
    );

    p.hitIds.push(victim.id);
    if (p.pierceRemaining > 0) p.pierceRemaining--;
    else ctx.projectiles.kill(p);
  }
}

/** Add `accel * DT` to the speed along the current heading, clamped to the missile cap. */
function accelerate(p: Projectile): void {
  const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
  if (speed <= 0) return;
  const cap = p.launchSpeed * MISSILE_MAX_SPEED_FACTOR;
  let next = speed + p.accel * DT;
  if (next > cap) next = cap;
  const scale = next / speed;
  p.vx *= scale;
  p.vy *= scale;
}

function steerToward(p: Projectile, target: Enemy): void {
  const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
  if (speed <= 0) return;
  const desired = angleTo(p.x, p.y, target.x, target.y);
  const current = angleTo(0, 0, p.vx, p.vy);
  const maxStep = p.turnRate * DT;
  const next = rotateTowardAngle(current, desired, maxStep);
  p.vx = cos(next) * speed;
  p.vy = sin(next) * speed;
}

/** Local copy of `math.rotateToward` semantics, kept here to avoid a circular import. */
function rotateTowardAngle(from: number, to: number, maxStep: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  if (delta > maxStep) delta = maxStep;
  else if (delta < -maxStep) delta = -maxStep;
  return from + delta;
}

/** True once the lob has reached or passed its snapshotted ground position this tick. */
function reachedTarget(p: Projectile, startX: number, startY: number): boolean {
  const remaining = distSq(p.x, p.y, p.targetX, p.targetY);
  const travelled = distSq(startX, startY, p.x, p.y);
  return remaining <= travelled || remaining <= p.radius * p.radius;
}

function detonate(p: Projectile, tower: Tower | undefined, ctx: FireContext): void {
  ctx.events.push({
    t: 'projectileHit',
    x: p.x,
    y: p.y,
    damage: p.damage,
    kind: p.kind,
    crit: p.crit,
    chainHop: 0,
    aoeRadius: p.aoeRadius,
    pierced: false,
    fromX: p.x - p.vx,
    fromY: p.y - p.vy,
  });

  ctx.grid.query(p.x, p.y, p.aoeRadius, ctx.enemies, ctx.scratch);
  const ids = ctx.scratch.slice();
  const input: DamageInput = { amount: p.damage, kind: p.kind, crit: p.crit, tower };
  for (let i = 0; i < ids.length; i++) {
    const victim = ctx.enemies.get(ids[i] as EntityId);
    if (!victim) continue;
    applyDamage(victim, input, ctx.tick, ctx.events);
    // A projectile can outlive the tower that fired it, if it was sold mid-flight.
    if (tower) applyOnHitHooks(ctx.hooks, tower, victim);
  }

  ctx.projectiles.kill(p);
}

/**
 * The first enemy this projectile hits on the segment it travelled this tick.
 *
 * Slow projectiles use a point test at the new position, which is cheaper and adequate.
 * Above `SWEPT_COLLISION_TILES_PER_TICK` the step is long enough to jump clean over a
 * small enemy, so the test becomes segment-vs-circle (R-05-14) — without this, a 2
 * tiles/tick bullet passes straight through a `size: 0.2` enemy and the tower looks
 * broken (T-8).
 *
 * "First" is resolved by distance along the segment, so a fast shot hits the nearest
 * enemy in its path rather than whichever happens to sit earliest in the array.
 */
function findCollision(
  p: Projectile,
  startX: number,
  startY: number,
  ctx: FireContext,
): Enemy | undefined {
  const dx = p.x - startX;
  const dy = p.y - startY;
  const stepSq = dx * dx + dy * dy;
  const swept = stepSq > SWEPT_COLLISION_TILES_PER_TICK * SWEPT_COLLISION_TILES_PER_TICK;

  // The query circle has to cover the whole segment, not just the endpoint, or the swept
  // test would only ever see enemies near where the projectile finished.
  const reach = swept ? Math.sqrt(stepSq) + p.radius : p.radius;
  const midX = swept ? (startX + p.x) / 2 : p.x;
  const midY = swept ? (startY + p.y) / 2 : p.y;
  ctx.grid.query(
    midX,
    midY,
    swept ? reach / 2 + p.radius + MAX_ENEMY_SIZE : reach + MAX_ENEMY_SIZE,
    ctx.enemies,
    ctx.scratch,
  );

  let best: Enemy | undefined;
  let bestT = Infinity;

  for (let i = 0; i < ctx.scratch.length; i++) {
    const id = ctx.scratch[i] as EntityId;
    if (p.hitIds.includes(id)) continue;
    // A homing shot only ever collides with the enemy it was fired at (R-05-13).
    if (p.turnRate > 0 && p.targetId !== NO_TARGET && id !== p.targetId) continue;

    const enemy = ctx.enemies.get(id);
    if (!enemy) continue;

    const hitRadius = p.radius + enemy.size;
    const hitRadiusSq = hitRadius * hitRadius;

    if (!swept) {
      if (distSq(p.x, p.y, enemy.x, enemy.y) <= hitRadiusSq) {
        const d = distSq(startX, startY, enemy.x, enemy.y);
        if (d < bestT || (d === bestT && best && enemy.id < best.id)) {
          best = enemy;
          bestT = d;
        }
      }
      continue;
    }

    const t = segmentHitParam(startX, startY, dx, dy, stepSq, enemy.x, enemy.y, hitRadiusSq);
    if (t === undefined) continue;
    if (t < bestT || (t === bestT && best && enemy.id < best.id)) {
      best = enemy;
      bestT = t;
    }
  }

  return best;
}

/** The largest `size` any enemy carries, used to pad the broad-phase query. */
const MAX_ENEMY_SIZE = 1;

/**
 * Where along the segment the projectile first comes within `hitRadiusSq` of a point,
 * as a parameter in [0, 1], or undefined if it never does.
 *
 * The closest approach is the projection of the enemy onto the segment, clamped to its
 * ends — clamping is what stops an enemy behind the muzzle from registering as a hit.
 */
function segmentHitParam(
  startX: number,
  startY: number,
  dx: number,
  dy: number,
  stepSq: number,
  px: number,
  py: number,
  hitRadiusSq: number,
): number | undefined {
  if (stepSq <= 0) return undefined;
  let t = ((px - startX) * dx + (py - startY) * dy) / stepSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const closestX = startX + dx * t;
  const closestY = startY + dy * t;
  return distSq(closestX, closestY, px, py) <= hitRadiusSq ? t : undefined;
}

/** Exported for the targeting system's range checks, which share the same definition. */
export { effectiveRange };
